/**
 * CampaignSkeletons - Campaign detail and form loading states
 */
import React from 'react';
import { Skeleton } from './BaseSkeleton';

export function CampaignDetailsSkeleton() {
  return (
    <div className="p-6">
      <div className="space-y-8">
        {/* Campaign Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <Skeleton className="h-8 w-64 mb-2" />
            <div className="flex items-center gap-1.5">
              <Skeleton className="h-4 w-4 rounded-full" />
              <Skeleton className="h-4 w-40" />
            </div>
          </div>
          <div className="flex flex-wrap gap-3">
            <Skeleton className="h-10 w-28 rounded-md" />
            <Skeleton className="h-10 w-28 rounded-md" />
          </div>
        </div>

        {/* Campaign Metrics - Updated to show only two cards */}
        <div className="grid gap-6 grid-cols-1 sm:grid-cols-2">
          <div className="rounded-xl border border-gray-200 bg-white p-5 dark:border-gray-800 dark:bg-white/[0.03] sm:p-6">
            <div className="space-y-1">
              <Skeleton className="h-4 w-32 mb-2" />
              <div className="flex items-center justify-between">
                <Skeleton className="h-8 w-16 sm:h-10" />
                <div className="rounded-md bg-gray-100/80 p-2 dark:bg-gray-800">
                  <Skeleton className="h-5 w-5 rounded" />
                </div>
              </div>
            </div>
          </div>
          <div className="rounded-xl border border-gray-200 bg-white p-5 dark:border-gray-800 dark:bg-white/[0.03] sm:p-6">
            <div className="space-y-1">
              <Skeleton className="h-4 w-32 mb-2" />
              <div className="flex items-center justify-between">
                <Skeleton className="h-8 w-16 sm:h-10" />
                <div className="rounded-md bg-gray-100/80 p-2 dark:bg-gray-800">
                  <Skeleton className="h-5 w-5 rounded" />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Campaign Info */}
        <div className="rounded-lg border border-gray-200 bg-white p-6 dark:border-gray-800 dark:bg-gray-900">
          <div className="flex justify-between items-center mb-4">
            <Skeleton className="h-6 w-48" />
            <Skeleton className="h-8 w-24 rounded-md" />
          </div>
          
          {/* Title and Description */}
          <div className="flex flex-col md:flex-row md:gap-8 mb-6">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <Skeleton className="h-4 w-4 rounded" />
                <Skeleton className="h-4 w-32" />
              </div>
              <Skeleton className="h-5 w-3/4" />
            </div>
            <div className="flex-1 mt-3 md:mt-0">
              <div className="flex items-center gap-2 mb-1">
                <Skeleton className="h-4 w-4 rounded" />
                <Skeleton className="h-4 w-32" />
              </div>
              <Skeleton className="h-5 w-full" />
            </div>
          </div>
          
          {/* Business Information */}
          <div className="border-t border-gray-200 dark:border-gray-800 pt-4 mb-4">
            <div className="flex flex-col md:flex-row md:gap-8">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <Skeleton className="h-4 w-4 rounded" />
                  <Skeleton className="h-4 w-24" />
                </div>
                <Skeleton className="h-5 w-36 mb-1" />
              </div>
              <div className="flex-1 mt-3 md:mt-0">
                <div className="flex items-center gap-2 mb-1">
                  <Skeleton className="h-4 w-4 rounded" />
                  <Skeleton className="h-4 w-32" />
                </div>
                <Skeleton className="h-5 w-36 mb-1" />
              </div>
            </div>
          </div>
          
          {/* Contact Information */}
          <div className="border-t border-gray-200 dark:border-gray-800 pt-4 mb-4">
            <div className="flex flex-col md:flex-row md:gap-8">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <Skeleton className="h-4 w-4 rounded" />
                  <Skeleton className="h-4 w-24" />
                </div>
                <Skeleton className="h-5 w-36 mb-1" />
              </div>
              <div className="flex-1 mt-3 md:mt-0">
                <div className="flex items-center gap-2 mb-1">
                  <Skeleton className="h-4 w-4 rounded" />
                  <Skeleton className="h-4 w-32" />
                </div>
                <Skeleton className="h-5 w-36 mb-1" />
              </div>
            </div>
          </div>
          
          {/* Media Section */}
          <div className="border-t border-gray-200 dark:border-gray-800 pt-4">
            <div className="flex flex-col md:flex-row md:gap-8">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-3">
                  <Skeleton className="h-4 w-4 rounded" />
                  <Skeleton className="h-4 w-32" />
                </div>
                <Skeleton className="h-40 w-40 rounded-md" />
              </div>
              <div className="flex-1 mt-3 md:mt-0">
                <div className="flex items-center gap-2 mb-3">
                  <Skeleton className="h-4 w-4 rounded" />
                  <Skeleton className="h-4 w-32" />
                </div>
                <Skeleton className="h-40 w-64 rounded-md" />
              </div>
            </div>
          </div>
        </div>
        
        {/* Campaign Analytics */}
        <div className="mt-8 grid gap-6 lg:grid-cols-2">
          <div className="rounded-lg border border-gray-200 bg-white p-5 dark:border-gray-800 dark:bg-gray-900 sm:p-6">
            <div className="mb-6">
              <Skeleton className="h-6 w-40 mb-2" />
              <div className="flex items-center justify-between">
                <Skeleton className="h-4 w-48" />
                <Skeleton className="h-6 w-16 rounded-full" />
              </div>
            </div>
            <div className="relative mt-6 h-[250px]">
              <Skeleton className="absolute bottom-0 h-[200px] w-full rounded-md" 
                       style={{ clipPath: 'polygon(0 40%, 10% 60%, 20% 45%, 30% 80%, 40% 30%, 50% 50%, 60% 40%, 70% 65%, 80% 30%, 90% 20%, 100% 35%, 100% 100%, 0 100%)' }} />
            </div>
          </div>
          <div className="rounded-lg border border-gray-200 bg-white p-5 dark:border-gray-800 dark:bg-gray-900 sm:p-6">
            <div className="mb-6">
              <Skeleton className="h-6 w-48 mb-2" />
              <div className="flex items-center justify-between">
                <Skeleton className="h-4 w-60" />
                <Skeleton className="h-6 w-16 rounded-full" />
              </div>
            </div>
            <div className="relative mt-6 h-[250px]">
              <Skeleton className="absolute bottom-0 h-[180px] w-full rounded-md" 
                       style={{ clipPath: 'polygon(0 70%, 10% 30%, 20% 65%, 30% 40%, 40% 60%, 50% 20%, 60% 50%, 70% 30%, 80% 60%, 90% 70%, 100% 45%, 100% 100%, 0 100%)' }} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export function CreateCampaignSkeleton() {
  return (
    <div className="space-y-6 transition-opacity duration-300 ease-in-out">
      {/* Draft section - simple bar */}
      <div className="w-full max-w-[800px] flex items-center gap-2 p-3 text-sm bg-gray-50 rounded-lg border border-gray-200 dark:bg-gray-800/50 dark:border-gray-800">
        <div className="flex items-center gap-2 w-full">
          <Skeleton className="h-4 w-4 bg-gray-300 rounded dark:bg-gray-700" delay={true} />
          <Skeleton className="h-4 w-24 bg-gray-300 rounded dark:bg-gray-700" delay={true} />
          <div className="ml-auto flex items-center gap-2">
            <Skeleton className="h-4 w-16 bg-gray-300 rounded dark:bg-gray-700" delay={true} />
            <Skeleton className="h-4 w-4 bg-gray-300 rounded dark:bg-gray-700" delay={true} />
          </div>
        </div>
      </div>
      
      {/* Main content grid */}
      <div className="grid grid-cols-1 lg:grid-cols-[800px,auto] gap-6 items-start">
        {/* Form container */}
        <div className="rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-6">
          <div className="space-y-6">
            {/* Step title */}
            <div>
              <Skeleton className="h-6 w-48 mb-3" delay={true} />
              <Skeleton className="h-4 w-full max-w-lg mb-2" delay={true} />
            </div>
            
            {/* Form fields */}
            <div className="space-y-4">
              <div>
                <Skeleton className="h-4 w-24 mb-2" delay={true} />
                <Skeleton className="h-10 w-full rounded-md" delay={true} />
              </div>
              
              <div>
                <Skeleton className="h-4 w-32 mb-2" delay={true} />
                <Skeleton className="h-36 w-full rounded-md" delay={true} />
              </div>
              
              {/* Image upload */}
              <div>
                <Skeleton className="h-4 w-24 mb-2" delay={true} />
                <div className="mt-2 flex items-center gap-4">
                  <Skeleton className="h-24 w-24 rounded-md" delay={true} />
                  <Skeleton className="h-10 w-32 rounded-md" delay={true} />
                </div>
              </div>
              
              {/* Category selection */}
              <div className="mt-6">
                <Skeleton className="h-4 w-32 mb-2" delay={true} />
                <div className="grid grid-cols-2 gap-3">
                  <Skeleton className="h-10 w-full rounded-md" delay={true} />
                  <Skeleton className="h-10 w-full rounded-md" delay={true} />
                </div>
              </div>
            </div>
            
            {/* Navigation buttons */}
            <div className="pt-4 border-t border-gray-200 dark:border-gray-800 flex justify-between">
              <Skeleton className="h-10 w-24 rounded-md" delay={true} />
              <div className="flex gap-2">
                <Skeleton className="h-10 w-32 rounded-md" delay={true} />
                <Skeleton className="h-10 w-24 rounded-md" delay={true} />
              </div>
            </div>
          </div>
        </div>
        
        {/* Phone preview */}
        <div className="hidden lg:block sticky top-24 h-[calc(100vh-6rem)] w-[500px] xl:block">
          <div className="flex flex-col items-center">
            <div className="scale-[0.8] origin-top xl:scale-[0.85] -mt-12">
              <div className="w-[375px] h-[812px] rounded-[55px] bg-gray-200 dark:bg-gray-800 animate-pulse overflow-hidden">
                <div className="flex flex-col h-full">
                  {/* Header area with campaign title */}
                  <div className="h-[150px] bg-gray-300 dark:bg-gray-700 flex flex-col items-center justify-center p-5">
                    <Skeleton className="h-6 w-48 mb-3 bg-gray-200/50 dark:bg-gray-800/50" delay={true} />
                    <Skeleton className="h-4 w-64 bg-gray-200/50 dark:bg-gray-800/50" delay={true} />
                  </div>
                  
                  {/* Content area with survey question */}
                  <div className="flex-1 p-6">
                    <Skeleton className="h-5 w-full mb-4" delay={true} />
                    <div className="space-y-3 mt-6">
                      <Skeleton className="h-12 w-full rounded-md" delay={true} />
                      <Skeleton className="h-12 w-full rounded-md" delay={true} />
                      <Skeleton className="h-12 w-full rounded-md" delay={true} />
                    </div>
                    <div className="flex justify-between mt-8">
                      <Skeleton className="h-10 w-10 rounded-full" delay={true} />
                      <Skeleton className="h-10 w-10 rounded-full" delay={true} />
                    </div>
                  </div>
                  
                  {/* Navigation dots */}
                  <div className="h-[60px] flex items-center justify-center">
                    <div className="flex gap-2">
                      <Skeleton className="h-2 w-8 rounded-full" delay={true} />
                      <Skeleton className="h-2 w-2 rounded-full" delay={true} />
                      <Skeleton className="h-2 w-2 rounded-full" delay={true} />
                      <Skeleton className="h-2 w-2 rounded-full" delay={true} />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export function CampaignSettingsSkeleton() {
  return (
    <div className="p-6">
      <div className="space-y-4">
        {/* Back button and header */}
        <div>
          <div className="flex items-center mb-4">
            <Skeleton className="h-9 w-32 rounded-md" />
          </div>
          
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">Edit Campaign</h1>
          <p className="mt-2 text-sm text-gray-600 dark:text-gray-400 mb-4">
            Update your campaign settings and customize your survey questions.
          </p>
          
          {/* Progress bar */}
          <div className="relative max-w-[800px] h-1 bg-gray-200 dark:bg-gray-800 mt-4">
            <div className="absolute inset-y-0 left-0 bg-primary-600 dark:bg-primary-400" style={{ width: '20%' }} />
          </div>
          
          {/* Step indicator */}
          <div className="flex items-center justify-between max-w-[800px] mt-2">
            <div className="text-xs text-gray-500 dark:text-gray-400">
              Step 1 of 5
            </div>
          </div>
        </div>

        {/* Main content grid */}
        <div className="grid grid-cols-1 lg:grid-cols-[800px,auto] gap-6 items-start">
          {/* Form container */}
          <div className="rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-6">
            <div className="space-y-6">
              {/* Step title */}
              <div>
                <Skeleton className="h-6 w-48 mb-3" />
                <Skeleton className="h-4 w-full max-w-lg mb-2" />
              </div>
              
              {/* Form fields */}
              <div className="space-y-4">
                <div>
                  <Skeleton className="h-4 w-24 mb-2" />
                  <Skeleton className="h-10 w-full rounded-md" />
                </div>
                
                <div>
                  <Skeleton className="h-4 w-32 mb-2" />
                  <Skeleton className="h-36 w-full rounded-md" />
                </div>
                
                {/* Image upload */}
                <div>
                  <Skeleton className="h-4 w-24 mb-2" />
                  <div className="mt-2 flex items-center gap-4">
                    <Skeleton className="h-24 w-24 rounded-md" />
                    <Skeleton className="h-10 w-32 rounded-md" />
                  </div>
                </div>
                
                {/* Theme selection */}
                <div className="mt-6">
                  <Skeleton className="h-4 w-32 mb-2" />
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    <Skeleton className="h-10 w-full rounded-md" />
                    <Skeleton className="h-10 w-full rounded-md" />
                    <Skeleton className="h-10 w-full rounded-md" />
                    <Skeleton className="h-10 w-full rounded-md" />
                  </div>
                </div>
              </div>
              
              {/* Navigation buttons */}
              <div className="pt-4 border-t border-gray-200 dark:border-gray-800 flex justify-between">
                <Skeleton className="h-10 w-24 rounded-md" />
                <div className="flex gap-2">
                  <Skeleton className="h-10 w-32 rounded-md" />
                  <Skeleton className="h-10 w-24 rounded-md" />
                </div>
              </div>
            </div>
          </div>
          
          {/* Phone preview */}
          <div className="hidden lg:block sticky top-24 h-[calc(100vh-6rem)] w-[500px] xl:block">
            <div className="flex flex-col items-center">
              <div className="scale-[0.8] origin-top xl:scale-[0.85] -mt-12">
                <div className="w-[375px] h-[812px] rounded-[55px] bg-gray-200 dark:bg-gray-800 animate-pulse overflow-hidden">
                  <div className="flex flex-col h-full">
                    {/* Header area with campaign title */}
                    <div className="h-[150px] bg-gray-300 dark:bg-gray-700 flex flex-col items-center justify-center p-5">
                      <Skeleton className="h-6 w-48 mb-3 bg-gray-200/50 dark:bg-gray-800/50" />
                      <Skeleton className="h-4 w-64 bg-gray-200/50 dark:bg-gray-800/50" />
                    </div>
                    
                    {/* Content area with survey question */}
                    <div className="flex-1 p-6">
                      <Skeleton className="h-5 w-full mb-4" />
                      <div className="space-y-3 mt-6">
                        <Skeleton className="h-12 w-full rounded-md" />
                        <Skeleton className="h-12 w-full rounded-md" />
                        <Skeleton className="h-12 w-full rounded-md" />
                      </div>
                      <div className="flex justify-between mt-8">
                        <Skeleton className="h-10 w-10 rounded-full" />
                        <Skeleton className="h-10 w-10 rounded-full" />
                      </div>
                    </div>
                    
                    {/* Navigation dots */}
                    <div className="h-[60px] flex items-center justify-center">
                      <div className="flex gap-2">
                        <Skeleton className="h-2 w-2 rounded-full" />
                        <Skeleton className="h-2 w-8 rounded-full" />
                        <Skeleton className="h-2 w-2 rounded-full" />
                        <Skeleton className="h-2 w-2 rounded-full" />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

